
import React, { Suspense, useCallback, useEffect, useState } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import KeyInsightsList from "../../components/KeyInsightsList";
import CentralPanel from "./CentralPanel";
import ProjectPulsePanel from "./ProjectPulsePanel";
import LeftFilterPanel from "../../components/AiAssistant/LeftFilterPanel";
import IntegrationsService from "../../services/Integrations/IntegrationsService";
import AiAssistantService from "../../services/AiAssistantService/AiAssistantService";
import { getUserData } from "../../Utils";
const AiAssistantPanel = React.lazy(() => import("../../components/AiAssistant/AiAssistantPanel"));

const VIEW_OPTIONS = [
    { label: "Operations", value: "operations" },
    { label: "Finance", value: "finance" },
];

export default function AiInsights() {
    const navigate = useNavigate();
    const [view, setView] = useState("operations");
    const [dropdownOpen, setDropdownOpen] = useState(false);

    // ── Finance state ──
    const [reportData, setReportData] = useState(null);
    const [financeLoading, setFinanceLoading] = useState(false);
    const [financeError, setFinanceError] = useState(null);
    const [keyInsightsList, setKeyInsightsList] = useState([]);
    const [keyInsightsLoading, setKeyInsightsLoading] = useState(false);

    // ── Operations state ──
    const [opsData, setOpsData] = useState({ tasks: [], projects: [], timeLogs: [], users: [], projectUsers: [] });
    const [opsLoading, setOpsLoading] = useState(false);
    const [opsError, setOpsError] = useState(null);

    // ── Shared state ──
    const [projects, setProjects] = useState([]);
    const [filters, setFilters] = useState({ dateRange: null, projectId: null, status: null });

    // Helper to extract error message from Amplify/API responses
    const extractErrorMessage = (error) => {
        // Try multiple paths where error messages can be stored
        if (error?.message) return error.message;
        if (error?.response?.data?.message) return error.response.data.message;
        if (typeof error === 'string') return error;
        if (error?.response?.statusText) return error.response.statusText;
        return "Error fetching data";
    };

    // Helper to determine if error is due to missing integration
    const isConnectionError = (error) => {
        return error?.includes("404") || error?.includes("not found") || error?.includes("No organization") || error?.includes("organization");
    };

    // Helper to get error message and details based on view
    const getErrorDetails = (error, viewType) => {
        const isConnError = isConnectionError(error);
        if (isConnError) {
            return {
                title: `${viewType === "finance" ? "Finance" : "Operations"} view unavailable`,
                message: viewType === "finance"
                    ? "You need to establish the Zoho Books connection first"
                    : "You need to establish the Zoho Projects connection first",
                showButton: true,
            };
        }
        return {
            title: viewType === "finance" ? "Finance view unavailable" : "Operations view unavailable",
            message: error || "Error fetching data",
            showButton: false,
        };
    };

    // ── Fetch Finance data ──
    useEffect(() => {
        if (view !== "finance") return;
        if (reportData) return; // already loaded
        const fetchFinance = async () => {
            setFinanceLoading(true);
            setFinanceError(null);
            try {
                const integrationService = new IntegrationsService();
                const iaService = new AiAssistantService();
                const [orgsSettled, projectsResult] = await Promise.allSettled([
                    integrationService.zohoGetOrganizations(),
                    integrationService.zohoProjectsList(),
                ]);
                if (projectsResult.status === "fulfilled") {
                    setProjects(projectsResult.value?.projects || []);
                }
                const orgs = orgsSettled.status === "fulfilled" ? orgsSettled.value : null;
                if (!orgs) throw new Error("No organization found");
                const organizationId = orgs.organizationId;
                if (!organizationId) throw new Error("No organization found");
                const report = await integrationService.zohoGetProfitAndLossReport(organizationId);
                setReportData(report);
                setKeyInsightsLoading(true);
                const currentUser = await Auth.currentAuthenticatedUser();
                const tenantId = currentUser?.attributes?.["custom:tenant_id"];
                if (!tenantId) throw new Error("Unable to retrieve tenant ID from user attributes");
                const payload = {
                    type: "AGENT_MAIN_PANEL",
                    context: { s3BasePath: "zoho_reports/" },
                    payload: {
                        tenantId: tenantId,
                        datasetFile: tenantId + "_hierarchy.csv",
                        indexFile: tenantId + "_summary.json",
                        s3BasePath: "zoho_reports/",
                        prompt: JSON.stringify(report),
                    },
                };
                const insightsRaw = await iaService.sendWebhook(payload);
                let insights = [];
                if (insightsRaw && Array.isArray(insightsRaw.insights)) {
                    insights = insightsRaw.insights.map((item) => ({
                        title: item.title || "",
                        confidence: item.confidence || "",
                        description: item.description || "",
                    }));
                }
                setKeyInsightsList(insights);
            } catch (err) {
                setFinanceError(extractErrorMessage(err));
            } finally {
                setFinanceLoading(false);
                setKeyInsightsLoading(false);
            }
        };
        fetchFinance();
    }, [view, reportData]);

    // ── Fetch Operations data ──
    useEffect(() => {
        if (view !== "operations") return;
        if (opsData.tasks.length > 0) return; // already loaded
        const fetchOps = async () => {
            setOpsLoading(true);
            setOpsError(null);
            try {
                const svc = new IntegrationsService();
                const [tasksRes, projectsRes, timeLogsRes, usersRes, projectUsersRes] = await Promise.allSettled([
                    svc.zohoProjectsTasks(),
                    svc.zohoProjectsList(),
                    svc.zohoProjectsTimeLogs(),
                    svc.zohoProjectsUsers(),
                    svc.zohoProjectsProjectUsers(),
                ]);

                // Check if main projects call failed (indicates no connection or validation error)
                if (projectsRes.status === "rejected") {
                    const errorMessage = extractErrorMessage(projectsRes.reason);
                    throw new Error(errorMessage);
                }

                const tasks = tasksRes.status === "fulfilled" ? (tasksRes.value?.tasks || []) : [];
                const projectsList = projectsRes.status === "fulfilled" ? (projectsRes.value?.projects || []) : [];
                const timeLogs = timeLogsRes.status === "fulfilled" ? (timeLogsRes.value?.time_logs || []) : [];
                const users = usersRes.status === "fulfilled" ? (usersRes.value?.users || []) : [];
                const projectUsers = projectUsersRes.status === "fulfilled" ? (projectUsersRes.value?.project_users || []) : [];

                setProjects(projectsList);
                setOpsData({ tasks, projects: projectsList, timeLogs, users, projectUsers });
            } catch (err) {
                setOpsError(extractErrorMessage(err));
            } finally {
                setOpsLoading(false);
            }
        };
        fetchOps();
    }, [view, opsData.tasks.length]);

    const handleFilterChange = useCallback((f) => setFilters(f), []);

    const loading = view === "finance" ? financeLoading : opsLoading;
    const error = view === "finance" ? financeError : opsError;

    return (
        <div style={{ display: "flex", height: "calc(100vh - 1px)", minHeight: 0 }}>
            {/* Left: Filter Panel */}
            <LeftFilterPanel
                mode={view}
                projects={projects}
                onFilterChange={handleFilterChange}
            />

            {/* Center: Data panel */}
            <div
                style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: "#f0f2f5",
                    minWidth: 0,
                }}
            >
                {/* Header */}
                <div style={{ padding: "20px 40px", backgroundColor: "#fff", boxShadow: "0 1px 3px rgb(0 0 0 / 0.1)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                        <h1 style={{ margin: 0, fontSize: 22 }}>AI Insights Dashboard</h1>
                        <p style={{ margin: 0, color: "#666", fontSize: 13 }}>Real-time profitability analysis powered by AI</p>
                    </div>

                    {/* Operations / Finance dropdown */}
                    <div style={{ position: "relative" }}>
                        <button
                            onClick={() => setDropdownOpen((o) => !o)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "8px 16px",
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                background: "#fff",
                                cursor: "pointer",
                                fontWeight: 600,
                                fontSize: 14,
                                color: "#111827",
                                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                            }}
                        >
                            <span style={{ fontSize: 16 }}>⚙️</span>
                            {VIEW_OPTIONS.find((o) => o.value === view)?.label}
                            <span style={{ fontSize: 10, color: "#6b7280", marginLeft: 2 }}>▼</span>
                        </button>
                        {dropdownOpen && (
                            <div
                                style={{
                                    position: "absolute",
                                    top: "calc(100% + 4px)",
                                    right: 0,
                                    background: "#fff",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: 8,
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                                    zIndex: 1000,
                                    minWidth: 160,
                                    overflow: "hidden",
                                }}
                            >
                                {VIEW_OPTIONS.map((opt) => (
                                    <div
                                        key={opt.value}
                                        onClick={() => { setView(opt.value); setDropdownOpen(false); }}
                                        style={{
                                            padding: "10px 16px",
                                            cursor: "pointer",
                                            fontSize: 14,
                                            fontWeight: view === opt.value ? 700 : 400,
                                            color: view === opt.value ? "#6366f1" : "#374151",
                                            background: view === opt.value ? "#eef2ff" : "transparent",
                                        }}
                                        onMouseOver={(e) => { if (view !== opt.value) e.currentTarget.style.background = "#f9fafb"; }}
                                        onMouseOut={(e) => { if (view !== opt.value) e.currentTarget.style.background = "transparent"; }}
                                    >
                                        {opt.label}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Content area */}
                <div style={{ flex: 1, padding: "32px 40px", overflowY: "auto" }}>
                    {loading ? (
                        <div style={{ color: "#6b7280", paddingTop: 40, textAlign: "center" }}>Loading {view === "operations" ? "operations data" : "report"}...</div>
                    ) : error ? (
                        (() => {
                            const errorDetails = getErrorDetails(error, view);
                            return (
                                <div style={{ padding: "24px", background: "#fee", border: "1px solid #fcc", borderRadius: "8px", color: "#c33" }}>
                                    <p style={{ fontWeight: 600, marginBottom: 8, fontSize: "16px" }}>⚠️ {errorDetails.title}</p>
                                    <p style={{ margin: 0, fontSize: "14px", color: "#d32f2f", marginBottom: "16px" }}>{errorDetails.message}</p>
                                    {errorDetails.showButton && (
                                        <button
                                            onClick={() => navigate("/integrations")}
                                            style={{
                                                padding: "10px 16px",
                                                background: "#d32f2f",
                                                color: "#fff",
                                                border: "none",
                                                borderRadius: "6px",
                                                cursor: "pointer",
                                                fontWeight: 600,
                                                fontSize: "14px",
                                                transition: "background 0.2s",
                                            }}
                                            onMouseOver={(e) => e.target.style.background = "#b71c1c"}
                                            onMouseOut={(e) => e.target.style.background = "#d32f2f"}
                                        >
                                            Go to Integrations
                                        </button>
                                    )}
                                </div>
                            );
                        })()
                    ) : view === "operations" ? (
                        <>
                            {/* Project Pulse header */}
                            <div style={{ marginBottom: 20 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    <span style={{ fontSize: 20, fontWeight: 800 }}>Project Pulse</span>
                                    <span style={{ fontSize: 12, background: "#dbeafe", color: "#1d4ed8", padding: "2px 10px", borderRadius: 20, fontWeight: 700 }}>Zoho Projects</span>
                                </div>
                                <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: 13 }}>
                                    Operational health from Zoho Projects: tasks, phases, users, issues, and delivery trends.
                                </p>
                            </div>
                            <ProjectPulsePanel data={opsData} filters={filters} />
                        </>
                    ) : (
                        <>
                            <CentralPanel reportData={reportData} dateRange={filters.dateRange} />
                            <KeyInsightsList insights={keyInsightsList} loading={keyInsightsLoading} />
                        </>
                    )}
                </div>
            </div>

            {/* Right: Chat panel */}
            <div
                style={{
                    width: 350,
                    background: "#fafbfc",
                    borderLeft: "1px solid #eee",
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Suspense fallback={<div style={{ padding: 24 }}>Loading Assistant...</div>}>
                    <AiAssistantPanel mode={view} />
                </Suspense>
            </div>
        </div>
    );
}
